import React, { FunctionComponent, PropsWithChildren } from "react";
import Link from "next/link";
import { PublicSettings } from "@/src/config/PublicSettings";
import { Utils } from "@/src/Utils";
import { ctaMapping } from "@/src/data/menus";

export interface LinkProps extends PropsWithChildren {
    href: string;
    className?: string;
    title?: string;
    target?: string;
    rel?: string;
    id?: string;
    [key: string]: any;
}

export const SmartLink: FunctionComponent<LinkProps> = props => {
    const { href, className, title, target: propTarget, rel: propRel, id, children, ...rest } = props;
    const urlData = Utils.normalizeURL(props.href);
    const isWhitelisted = PublicSettings.LINK_FOLLOW_DOMAIN_WHITELIST.some(domain => domain && urlData.normalizedURL.indexOf(domain) !== -1);
    const hasFileExtension = /\.[^\/\s]+$/.test(urlData.normalizedURL);

    const target = propTarget ?? (hasFileExtension || (urlData.isExternal && urlData.isWebURL) ? "_blank" : undefined);
    const rel = propRel ?? (urlData.isExternal && !isWhitelisted ? "nofollow noopener" : undefined);
    const ariaLabel = props["aria-label"] ?? title;

    const content = React.Children.count(children) === 1 ? children : <span>{children}</span>;

    let getCtaType: () => string | null = () => null;

    if (ctaMapping) {
        getCtaType = () => {
            if (id && id in ctaMapping.idMappings) {
                return ctaMapping.idMappings[id as keyof typeof ctaMapping.idMappings];
            }

            for (const [path, ctaName] of Object.entries(ctaMapping.pathMappings)) {
                if (urlData.normalizedURL.includes(path)) {
                    return ctaName;
                }
            }

            for (const { pattern, name } of ctaMapping.patternMappings) {
                if (pattern.test(urlData.normalizedURL)) {
                    return name;
                }
            }

            return null;
        };
    }

    const ctaType = getCtaType();

    const linkProps = {
        id,
        href: urlData.normalizedURL,
        target,
        rel,
        className,
        title,
        "aria-label": ariaLabel,
        ...(ctaType ? { "data-dd-action-name": ctaType } : {}),
        ...rest
    };

    const pathname = Utils.extractPathname(urlData.normalizedURL);
    const isQuoteLink = pathname === "/quote";

    if (isQuoteLink) {
        return <a {...linkProps}>{content}</a>;
    }

    return <Link {...linkProps}>{content}</Link>;
};
